import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../components-v2/Button';
import { AppDrawerContent } from '../../../../../components-v2/Drawer/AppDrawer';
import { Column, Row } from '../../../../../components-v2/PageLayout';
import { Checkbox } from '../../../../../components/Checkbox';
import { useTableColumnVisibilityContext } from '../../context/TableColumnVisibilityContext';
import { ColumnGroup } from '../../hooks/useTableColumnVisibility';
import { styleSheet } from './styles';

// Rendered in order; empty groups are skipped.
const COLUMN_GROUPS: { id: ColumnGroup; label: string }[] = [
	{ id: 'contactDetails', label: 'Contact Details' },
	{ id: 'connections', label: 'Connections' },
];

export const TableSettingsAppDrawerContent: React.FC = () => {
	const { columns, visibleColumnCount, maxVisibleColumns, canAddMoreColumns, updateColumnVisibility, resetToDefaults } =
		useTableColumnVisibilityContext();

	return (
		<AppDrawerContent value='table-settings'>
			<Column styles={[styleSheet.container]} scrollable>
				<Column styles={[styleSheet.section]}>
					<h3 className={css(styleSheet.sectionTitle)}>Table Settings</h3>
					<h4 className={css(styleSheet.sectionSubTitle)}>Column Visibility</h4>
					<p className={css(styleSheet.sectionDescription)}>
						Choose which columns to display in the contacts table. You can show up to {maxVisibleColumns} columns at
						once.
					</p>
					<Column styles={[styleSheet.columnList]}>
						{COLUMN_GROUPS.map(group => {
							const groupColumns = columns.filter(column => column.group === group.id);
							if (!groupColumns.length) {
								return null;
							}

							return (
								<Column key={group.id} styles={[styleSheet.columnGroup]}>
									<h5 className={css(styleSheet.groupLabel)}>{group.label}</h5>
									{groupColumns.map(column => {
										const isDisabled = column.isRequired || (!column.isVisible && !canAddMoreColumns);
										const showLimitMessage = !column.isVisible && !canAddMoreColumns && !column.isRequired;

										return (
											<Row key={column.id} styles={[styleSheet.columnItem]}>
												<Checkbox
													id={`column-${column.id}`}
													checked={column.isVisible}
													disabled={isDisabled}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
														updateColumnVisibility(column.id, e.target.checked)
													}
												>
													<Column styles={[styleSheet.columnLabelContainer]}>
														<span className={css(styleSheet.columnLabel)}>
															{column.label}
															{column.isRequired && (
																<span className={css(styleSheet.requiredIndicator)}> (Required)</span>
															)}
														</span>
														{showLimitMessage && (
															<span className={css(styleSheet.limitMessage)}>
																Maximum of {maxVisibleColumns} columns allowed
															</span>
														)}
													</Column>
												</Checkbox>
											</Row>
										);
									})}
								</Column>
							);
						})}
					</Column>
				</Column>

				<Column styles={[styleSheet.footer]}>
					<Row styles={[styleSheet.footerStats]}>
						<span className={css(styleSheet.statsText)}>
							{visibleColumnCount} of {maxVisibleColumns} columns visible
						</span>
					</Row>
					<Button variant='secondary' onClick={resetToDefaults} styles={[styleSheet.resetButton]}>
						Reset to Defaults
					</Button>
				</Column>
			</Column>
		</AppDrawerContent>
	);
};
